import React, { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, ChevronRightIcon, PlayCircleIcon } from '@heroicons/react/24/outline'
import { DashboardPhaseSummary } from '../../types'

interface PhaseSelectionModalV3Props {
    isOpen: boolean
    onClose: () => void
    phases: DashboardPhaseSummary[]
}

const PhaseSelectionModalV3: React.FC<PhaseSelectionModalV3Props> = ({
    isOpen,
    onClose,
    phases,
}) => {
    // Group phases by customer
    const groupedPhases = phases.reduce((acc, phase) => {
        if (!acc[phase.customerName]) {
            acc[phase.customerName] = []
        }
        acc[phase.customerName].push(phase)
        return acc
    }, {} as Record<string, DashboardPhaseSummary[]>)

    // Convert to array and sort by customer name
    const sortedGroups = Object.entries(groupedPhases).sort((a, b) => a[0].localeCompare(b[0]))

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-8"
                            enterTo="opacity-100 translate-y-0"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-8"
                        >
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white shadow-2xl transition-all">
                                <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-8 py-6 text-white relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl"></div>
                                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-8 -mb-8 blur-xl"></div>

                                    <div className="flex justify-between items-start relative z-10">
                                        <div>
                                            <Dialog.Title as="h3" className="text-2xl font-bold">
                                                Select Project
                                            </Dialog.Title>
                                            <p className="text-primary-100 mt-1 text-sm">Pick a migration phase to continue</p>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="rounded-full p-2 bg-white/10 hover:bg-white/20 text-white transition-colors backdrop-blur-sm"
                                        >
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="max-h-[60vh] overflow-y-auto bg-gray-50">
                                    {phases.length === 0 ? (
                                        <div className="text-center py-12 px-6">
                                            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <PlayCircleIcon className="w-8 h-8 text-gray-400" />
                                            </div>
                                            <p className="text-gray-900 font-medium mb-1">No active projects</p>
                                            <p className="text-gray-500 text-sm mb-6">Start by creating a new customer phase.</p>
                                            <Link
                                                to="/customers"
                                                className="btn-primary inline-flex"
                                                onClick={onClose}
                                            >
                                                Go to Customers
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-gray-100">
                                            {sortedGroups.map(([customerName, customerPhases]) => (
                                                <div key={customerName} className="bg-white mb-2 last:mb-0 shadow-sm">
                                                    <div className="px-6 py-3 bg-gray-50/50 border-b border-gray-100 sticky top-0 z-10 backdrop-blur-sm">
                                                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{customerName}</h4>
                                                    </div>
                                                    <div className="divide-y divide-gray-50">
                                                        {customerPhases.map((phase) => (
                                                            <Link
                                                                key={`${phase.customerId}-${phase.phaseId}`}
                                                                to={`/customers/${phase.customerId}/phases/${phase.phaseId}/wizard`}
                                                                className="flex items-center justify-between px-6 py-4 hover:bg-primary-50 group transition-colors duration-200"
                                                                onClick={onClose}
                                                            >
                                                                <div>
                                                                    <div className="flex items-center gap-3 mb-1">
                                                                        <span className={`w-2 h-2 rounded-full ${phase.status === 'completed'
                                                                                ? 'bg-green-500 shadow-green-200 shadow-sm'
                                                                                : phase.status === 'failed'
                                                                                    ? 'bg-red-500 shadow-red-200 shadow-sm'
                                                                                    : 'bg-blue-500 shadow-blue-200 shadow-sm'
                                                                            }`}></span>
                                                                        <h5 className="font-semibold text-gray-900 group-hover:text-primary-700">{phase.phaseName}</h5>
                                                                    </div>
                                                                    <p className="text-sm text-gray-500 pl-5">
                                                                        {phase.wizard?.nextStepLabel || 'Ready to start'}
                                                                    </p>
                                                                </div>
                                                                <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-primary-500 transition-colors" />
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="p-4 bg-gray-50 border-t border-gray-200 text-center">
                                    <Link to="/customers" onClick={onClose} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                                        View all customers
                                    </Link>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}

export default PhaseSelectionModalV3
